import { useState } from 'react'

import { Wizard } from '@factorialco/deprecated-design-system'
import {
  TrainingRequestManageEveryoneDocument,
  TrainingsTrainingRequestStatusEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useWizardSteps } from './WizardSteps/useWizardSteps'
import { WizardData } from './types'

interface WizardConfig {
  trainingId?: number
  fromCatalog?: boolean
  allowDraft?: boolean
}

interface ActionProps {
  onClose: () => void
  onSubmit: (
    status?: TrainingsTrainingRequestStatusEnum,
    data?: WizardData,
    isDraft?: boolean
  ) => Promise<void>
}

interface UserProps {
  employeeId?: number
}
interface Props {
  wizardConfig?: WizardConfig
  user?: UserProps
  actions: ActionProps
  getManagedIds?: (manageEveryone: boolean) => {
    managedEmployeeIds: number[]
    excludedEmployeeIds: number[]
  }
}

export const WizardComponent = ({
  wizardConfig = { fromCatalog: false, trainingId: undefined, allowDraft: true },
  user,
  actions: { onClose, onSubmit },
  getManagedIds,
}: Props) => {
  const { employee } = useCurrentSession()
  const { employeeId } = user || {}
  const targetEmployeeId = employeeId ?? employee?.id
  const { fromCatalog = false, trainingId, allowDraft = true } = wizardConfig || {}
  const amplitudeEvent = fromCatalog
    ? trackingEvents.training.trainingRequest.addTrainingRequest.fromCatalog
    : trackingEvents.training.trainingRequest.addTrainingRequest.newProposal

  const initialData: WizardData = {
    status: 'review',
    subsidized: false,
    employeeIds: targetEmployeeId ? [targetEmployeeId] : [],
    competencyIds: [],
    trainingId,
  }

  // so there's this bug that for some reason, the wizard when is shown without loading anything before it does not show at all...
  // I can't figure out why, and happened since the begining, I think it has to do something with the entrance animation
  //  which I think is not working at all. Having it now separated and bundled outside the code makes it even harder to debug
  const { data: manageEveryoneData, loading: loadingCompetencies } = useQuery(
    TrainingRequestManageEveryoneDocument,
    { fetchPolicy: 'network-only' }
  )

  const manageEveryone =
    !!manageEveryoneData?.trainings?._policies?.trainingsRequestsManageEveryone?.can

  const { managedEmployeeIds, excludedEmployeeIds } = getManagedIds
    ? getManagedIds(manageEveryone)
    : { managedEmployeeIds: [], excludedEmployeeIds: [] }

  const [wizardData, setWizardData] = useState<WizardData>(initialData)

  const handleSubmit = async (
    status: TrainingsTrainingRequestStatusEnum = 'review',
    data: WizardData = wizardData,
    isDraft?: boolean
  ) => {
    return onSubmit(status, data, isDraft)
  }

  const handleOnDataChanged = (newData: WizardData) => {
    setWizardData({ ...wizardData, ...newData })
  }

  const handleDraftSubmit = async (data: WizardData) => {
    return handleSubmit('draft', data, true)
  }

  const steps = useWizardSteps(
    {
      fromCatalog,
      trainingId,
      allowDraft,
    },
    {
      excludedEmployeeIds,
      managedEmployeeIds,
      manageEveryone,
    },
    handleDraftSubmit,
    amplitudeEvent
  )

  if (loadingCompetencies) {
    return <></>
  }

  return (
    <Wizard.Modal
      title={i18n.t('trainings.training_requests.create.training_request_title')}
      icon="document-folder-board-edit"
      initialData={wizardData}
      steps={steps}
      onSubmit={() => handleSubmit()}
      onClose={onClose}
      onDataChanged={handleOnDataChanged}
    />
  )
}
